import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, Grid, Checkbox, FormLabel } from '@mui/material';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { accStatementDetailsValidator } from './AccountStatementDetailsContainer';
import { useMutation } from 'hooks/apollo';
import { ADD_UPDATE_ACCOUNT_STATEMENT_DETAIL } from '../../mutations';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';

const newStatementDetail = {
  configDetailId: null,
  accountSymbol: '',
  accountName: '',
  accountTypeId: null,
  accountClass: '',
  financialIndicatorId: null,
  orderNo: null,
  attributName: '',
  visibleYN: true,
  readOnlyYN: false,
  isInEditing: true
}

const AccountStatementDetailsModalComponent = ({ isOpen, handleCloseModal, accountTypes, financialIndicatorList, configId }) => {
  const { t } = useTranslation();
  const [statementDetailLens, dirtyInfo] = useChangeTrackingLens(newStatementDetail);
  const statementDetail = statementDetailLens |> get;
  const [saving, setSaving] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(accStatementDetailsValidator);
  
  useEffect(() => {
    validate(statementDetailLens |> get, dirtyInfo);
  }, [dirtyInfo, statementDetailLens, validate]);

  const [saveAccountStatementDetail] = useMutation(ADD_UPDATE_ACCOUNT_STATEMENT_DETAIL, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    if (!validate(statementDetail)) return;
    setSaving(true);
    
    saveAccountStatementDetail({ variables: { input: {
      configDetailId: parseInt(statementDetail.configDetailId),
      configId: parseInt(configId),
      accountSymbol: statementDetail.accountSymbol,
      accountName: statementDetail.accountName,
      accountTypeId: parseInt(statementDetail.accountTypeId),
      financialIndicatorId: parseInt(statementDetail.financialIndicatorId),
      attributName: statementDetail.attributName,
      visibleYN: statementDetail.visibleYN,
      readOnlyYN: statementDetail.readOnlyYN
    } } });
  }, [configId, saveAccountStatementDetail, statementDetail, validate]);


  return (
    <Dialog open={isOpen} maxWidth='sm' scroll={'body'} onClose={handleCloseModal} TransitionProps={{ exit: false }}>
      <DialogContent>
        { isOpen && 
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={statementDetailLens.accountName |> get}
                variant='standard'
                onChange={statementDetailLens.accountName |> set}
                error={!isValid(validation.accountName)}
                helperText={getErrors(validation.accountName)}
                label={t('AccountStatement.Details.AccountName')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={statementDetailLens.accountSymbol |> get}
                variant='standard'
                onChange={statementDetailLens.accountSymbol |> set}
                error={!isValid(validation.accountSymbol)}
                helperText={getErrors(validation.accountSymbol)}
                label={t('AccountStatement.Details.AccountSymbol')}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                valueKey='id'
                labelKey='name'
                value={accountTypes?.find(x => x.id === statementDetail.accountTypeId)}
                options={accountTypes}
                onChange={event => (statementDetailLens.accountTypeId |> set)(event.id)}
                error={!isValid(validation.accountTypeId)}
                helperText={getErrors(validation.accountTypeId)}
                label={t('AccountStatement.Details.AccountType')}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                valueKey='id'
                labelKey='name'
                value={financialIndicatorList?.find(x => x.id === statementDetail.financialIndicatorId)}
                options={financialIndicatorList}
                label={t('AccountStatement.Details.FinancialIndicatorName')}
                onChange={event => (statementDetailLens.financialIndicatorId |> set)(event.id)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={statementDetailLens.attributName |> get}
                variant='standard'
                onChange={statementDetailLens.attributName |> set}
                error={!isValid(validation.attributName)}
                helperText={getErrors(validation.attributName)}
                label={t('AccountStatement.Details.AttributName')}
              />
            </Grid>
            <Grid container item xs={12} justifyContent='flex-start'>
              <Grid item xs={4}>
                <Checkbox
                  checked={statementDetail.visibleYN || false}
                  onChange={() => set(statementDetailLens.visibleYN)(!statementDetail.visibleYN)}
                />
                <FormLabel>{t('AccountStatement.Details.Visible')}</FormLabel>
              </Grid>
              <Grid item xs={4}>
                <Checkbox
                  checked={statementDetail.readOnlyYN || false}
                  onChange={() => set(statementDetailLens.readOnlyYN)(!statementDetail.readOnlyYN)}
                />
                <FormLabel>{t('AccountStatement.Details.ReadOnly')}</FormLabel>
              </Grid>
            </Grid>
            <Grid container item xs={12} justifyContent='flex-end'>
              <Button onClick={handleSave} sx={{ mr: 1 }} disabled={saving}>
                {saving ? t('Saving') : t('Save')}
              </Button>
              <Button onClick={handleCloseModal}>{t('Close')}</Button>
            </Grid>
          </Grid>
        }
      </DialogContent>
    </Dialog>
  );
};

AccountStatementDetailsModalComponent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  accountTypes: PropTypes.array.isRequired,
  configId: PropTypes.number,
  financialIndicatorList: PropTypes.array.isRequired
};

export default AccountStatementDetailsModalComponent;
